import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import makeUpMenuData from '@/constant/menu';
import { type listType, type itemProps } from '@/components/NavTabs/types';
import { useRoute } from 'vue-router';
import { CheckSourceType } from './types';
import { fetchIssue } from '@/api/issue';
import { mergeDetail } from '@/api/merge';
import { getTagDetail } from '@/api/tags';
import { getReleaseDetail } from '@/api/release';
import { getMilestonesByIid } from '@/api/milestone';
import { getClaInfoDetail } from '@/api/cla';
import { reqCatch } from '@/utils/catch';
import { useRepoId, useGetNamespace } from '@/utils/hooks/useRepoId';
import { useAccountStore } from '@/stores/user';
import { errorCodeType } from '@/utils/types';
import { getSearchCommonConfig } from '@/api/repo';
import { currentSiteName } from '@/utils/tools';
import { i18n } from '@/i18n';
const GLOABL_PAGE_SIZE_KEY = 'global-page-size';

export const useGlobalInfoStore = defineStore('globalInfo', () => {
  const { t } = i18n.global;
  const menu = makeUpMenuData();

  const route = useRoute();
  const globalMenuInfo = ref<listType>(menu); // 全部菜单信息
  const menuType = ref<any>(route?.meta?.type || 'repo'); // 当前菜单类型
  const isNotFound = ref(false); // 是否展示404页面
  const isNoAccess = ref(false); // 是否展示403页面
  const isPendingReview = ref(false); // 是否展示待审核页面
  const showTools = ref(true); // 是否展示工具栏
  const namespaceType = ref(-1); // 0 - 用户 1 - 组织
  const globalTheme = ref('light'); // 网站主题
  const httpStatus = ref<number>(200); // http状态码
  const isSignIn = ref(false); // 是否签到

  const isShowSidebar = ref(false); // 是否展示侧边栏
  const isShowDownloadPopup = ref(false); // 是否展示下载弹窗

  const menuInfo = computed(() => {
    const MenuList = globalMenuInfo.value[(menuType.value)] || [];
    // 当前菜单
    return MenuList;
  });
  const pageSize = ref<number>(Number(localStorage.getItem(GLOABL_PAGE_SIZE_KEY)) || 10);
  const pageSizeOptions = ref<number[]>([10, 20, 50]);

  // 设置单类菜单信息
  const setNamespaceType = (val: number) => {
    namespaceType.value = val;
  };

  const setHttpStatus = (val: number) => {
    httpStatus.value = val;
  };

  // 设置全部菜单信息
  const setGlobalMenuInfo = (val: any) => {
    const oldGroupMenu = globalMenuInfo.value[menuType.value];
    const newGroupMenu = val[menuType.value];
    let groupMenu = [];
    if (oldGroupMenu && newGroupMenu) {
      groupMenu = newGroupMenu.map((item:any) => {
        const target = oldGroupMenu.find((oldItem) => oldItem.id === item.id) as any;
        if (target && target.num) {
          return {
            ...item,
            num: target.num
          };
        }
        return {
          ...item
        };
      });
    }
    globalMenuInfo.value = {
      ...globalMenuInfo.value,
      ...val,
      [menuType.value]: groupMenu
    };
  };

  /**
   * 设置菜单类型
   * @param val 类型
   * @param routeNamespace 路由中的命名空间（某些地方获取不到，需要手动传入）
   */
  const setMenuType = async (val: string) => {
    if (val) {
      menuType.value = val;
    }
  };

  /**
   * 设置菜单标题旁的数字（如：mr数量，成员数量）
   * @param id 菜单id
   * @param val 增加或减少的数量
   * @param actionType 类型 add增加 reduce减少 equal等于
   */
  const updateMenuNum = (id: string, val: number, actionType: 'add' | 'reduce' | 'equal' = 'equal') => {
    const list: itemProps[] = globalMenuInfo.value[menuType.value] || [];
    list.forEach(item => {
      if (item.id === id) {
        if (actionType === 'equal') {
          item.num = val;
        }
        if (actionType === 'add') {
          item.num = item.num ? item.num + val : val; // 新增前判断num是否存在
        }
        if (actionType === 'reduce') {
          item.num = item.num <= 0 ? 0 : item.num - val; // 防止刷成负数
        }
      }
    });
  };

  // 设置404界面
  const setIsNotFound = (val: boolean) => {
    isNotFound.value = val;
    val && setHttpStatus(errorCodeType.NOT_FOUND);
    if (val) {
      document.title = `${currentSiteName()} - ${t('page.t.opeSouComOfGloDev')}}`;
    }
  };

  const setIsNoAccess = (val: boolean) => {
    isNoAccess.value = val;
    val && setHttpStatus(errorCodeType.NO_ACCESS);
    if (val) {
      document.title = `${currentSiteName()} - ${t('page.t.opeSouComOfGloDev')}}`;
    }
  };

  const setIsPendingReview = (val: boolean) => {
    isPendingReview.value = val;
    val && setHttpStatus(errorCodeType.PENDING_REVIEW);
  };

  const setShowTools = (val: boolean) => {
    showTools.value = val;
  };

  /**
   * TODO: 设置主题，模仿github用于设置仓库项目markdown图片；
   * 前端暂时固定单一主题，后续可能会提供设置功能
   */
  const setGlobalTheme = (val: string) => {
    globalTheme.value = val;
  };

  /**
   * 检测是资源是否通过审核（issue、pr、讨论...）
   * @param type
   */
  const checkSource = async (type: CheckSourceType, to) => {
    let res = {};
    let params = {};
    const { namespace } = useGetNamespace(to.params, '%2F');
    const { repoId } = useRepoId('%2F', namespace.value, to.params.repoName);
    const { accountInfo } = useAccountStore();

    switch (type) {
      case CheckSourceType.issue:
        params = {
          project_id: repoId.value,
          issue_iid: to.params.serialNumber
        };
        res = await reqCatch(fetchIssue, params);
        break;
      case CheckSourceType.pr:
        params = {
          view: 'simple',
          repoId: repoId.value,
          iid: to.params.mergeId
        };
        res = await reqCatch(mergeDetail, params);
        break;
      case CheckSourceType.tag:
        params = {
          repoId: repoId.value,
          tag_name: to.params.tagName
        };
        res = await reqCatch(getTagDetail, params);
        break;
      case CheckSourceType.release:
        params = {
          repoId: repoId.value,
          tag_name: to.params.tagName
        };
        res = await reqCatch(getReleaseDetail, params);
        break;
      case CheckSourceType.milestone:
        params = {
          project_id: repoId.value,
          milestone_iid: to.query?.iid
        };
        res = await reqCatch(getMilestonesByIid, params);
        break;
      case CheckSourceType.cla:
        params = {
          cla_id: to.params.claId,
          uid: accountInfo.id
        };
        res = await reqCatch(getClaInfoDetail, params);
        if (res.error?.error_code === 1044) { // cla特殊处理,跳转404
          setIsNotFound(true);
          location.href = '/404';
        }
        break;
      default:
        break;
    }

    const errorCode = res?.error?.http_status;
    switch (errorCode) {
      case errorCodeType.NOT_FOUND: // 路径不存在或审核不通过
        setIsNotFound(true);
        break;
      case errorCodeType.NO_ACCESS: // 无权限访问
        setIsNoAccess(true);
        break;
      case errorCodeType.PENDING_REVIEW: // 审核中
        setIsPendingReview(true);
        break;

      default:
        break;
    }
  };

  const setPageSize = (val: number) => {
    if (val) {
      localStorage.setItem(GLOABL_PAGE_SIZE_KEY, String(val));
      pageSize.value = val;
    }
  };

  const setPageSizeOptions = (val: number[]) => {
    pageSizeOptions.value = val;
  };

  const setIsSignIn = (val: boolean) => {
    isSignIn.value = val;
  };

  const getOverlayConfig = async () => {
    const res = await getSearchCommonConfig();
    const { 'show-sidebar': showSidebar, 'download_popup': downloadPopup } = res?.data || {};
    isShowSidebar.value = showSidebar;
    isShowDownloadPopup.value = downloadPopup;
  };

  return {
    globalMenuInfo,
    menuInfo,
    menuType,
    isNotFound,
    isNoAccess,
    showTools,
    namespaceType,
    globalTheme,
    isPendingReview,
    httpStatus,
    pageSize,
    pageSizeOptions,
    isSignIn,
    setMenuType,
    updateMenuNum,
    setGlobalMenuInfo,
    setIsNotFound,
    setIsNoAccess,
    setShowTools,
    setNamespaceType,
    setIsPendingReview,
    checkSource,
    setGlobalTheme,
    setHttpStatus,
    setPageSize,
    setPageSizeOptions,
    setIsSignIn,
    isShowSidebar,
    isShowDownloadPopup,
    getOverlayConfig
  };
});
